import styled from "@emotion/styled";
import { fadeInAndSlideUp, scaleIn, shimmer } from "styles/animations";
import { Flex, Text } from "styles/utility-components";

import Button from "components/ui/button/Button";
import Collapsible from "components/ui/collapsible/Collapsible";
import Tooltip from "components/ui/tooltip/Tooltip";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { pluralise } from "lib/utils";
import { FailedFile } from "./DocumentUploadProgressProvider";

type UploadProgressBarProps = {
  processedFiles: number;
  totalFiles: number;
  failedFiles?: FailedFile[];
  syncFailed?: boolean;
  onDismiss?: () => void;
};

export function UploadProgressBar({
  processedFiles,
  totalFiles,
  failedFiles = [],
  syncFailed = false,
  onDismiss,
}: UploadProgressBarProps) {
  const isWaiting = totalFiles === 0 && !syncFailed;
  const isComplete = !isWaiting && !syncFailed && processedFiles >= totalFiles && totalFiles > 0;
  const percent = totalFiles > 0 ? Math.min(100, (processedFiles / totalFiles) * 100) : 0;

  return (
    <FloatingContainer aria-label="Upload progress container">
      <Header>
        <Flex gap={8} alignItems="center">
          {isComplete && (
            <CheckIconWrapper>
              <UIcon icon="checkCircle" size={20} color="sysSuccessDefault" />
            </CheckIconWrapper>
          )}
          {syncFailed && <UIcon icon="alertCircle" size={20} color="sysDestructiveDefault" />}
          <StatusText>
            {syncFailed
              ? "Sync failed"
              : isComplete
                ? "Upload complete"
                : isWaiting
                  ? "Syncing files…"
                  : `Processing ${processedFiles} of ${totalFiles} files`}
          </StatusText>
        </Flex>
        {onDismiss && (
          <Button
            icon="xClose"
            iconProps={{ size: 20, color: "sysTextSecondary" }}
            buttonType="tertiary"
            onClick={onDismiss}
            aria-label="Dismiss"
          />
        )}
      </Header>
      <ProgressContainer>
        <ProgressBackground>
          <ProgressFill
            percent={isComplete || syncFailed ? 100 : isWaiting ? 100 : percent}
            isComplete={isComplete}
            isWaiting={isWaiting}
            isFailed={syncFailed}
          />
        </ProgressBackground>
      </ProgressContainer>
      {failedFiles.length > 0 && (
        <FailedFilesCollapsibleWrapper>
          <Collapsible
            title={
              <Text color="sysDestructiveDefault">
                {pluralise(failedFiles.length, "file")} failed
              </Text>
            }
            icon="alertCircle"
            iconProps={{ color: "sysDestructiveDefault" }}
            defaultOpen={false}
            dividerOnOpen={false}
            headerTriggerProps={{ padding: "0 10px 0 2px" }}
            contentWrapperStyles={{ padding: "8px 0 0 0" }}
          >
            <FailedFilesList>
              {failedFiles.map((file) => (
                <Tooltip
                  key={file.recordId}
                  content={file.failureReason || "Processing failed"}
                  side="left"
                >
                  <FailedFileItem
                    tabIndex={0}
                    aria-label={`${file.fileName}: ${file.failureReason || "Processing failed"}`}
                  >
                    <UIcon icon="file06" size={14} />
                    <FailedFileName>{file.fileName}</FailedFileName>
                  </FailedFileItem>
                </Tooltip>
              ))}
            </FailedFilesList>
          </Collapsible>
        </FailedFilesCollapsibleWrapper>
      )}
    </FloatingContainer>
  );
}

const FloatingContainer = styled.div(({ theme }) => ({
  position: "fixed",
  bottom: 24,
  right: 24,
  width: 360,
  maxWidth: "calc(100vw - 48px)",
  zIndex: 1000,
  backgroundColor: theme.colors.sysBackgroundDefault,
  borderRadius: 8,
  boxShadow: "0px 4px 12px 0px rgba(0, 0, 0, 0.12)",
  padding: 16,
  animation: `${fadeInAndSlideUp} 0.3s ease-out`,
  "@media (max-width: 640px)": {
    bottom: 16,
    right: 16,
    left: 16,
    width: "auto",
    maxWidth: "none",
  },
}));

const Header = styled(Flex)({
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: 12,
});

const StatusText = styled(Text)(({ theme }) => ({
  fontSize: 14,
  fontWeight: 500,
  lineHeight: 1.5,
  color: theme.colors.sysTextDefault,
}));

const CheckIconWrapper = styled(Flex)({
  animation: `${scaleIn} 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)`, // Bouncy animation
  flex: "0 0 auto",
});

const ProgressContainer = styled.div({
  width: "100%",
});

const ProgressBackground = styled.div(({ theme }) => ({
  width: "100%",
  height: 8,
  backgroundColor: theme.colors.sysPrimarySubtle,
  borderRadius: 16,
  overflow: "hidden",
}));

const ProgressFill = styled.div<{
  percent: number;
  isComplete: boolean;
  isWaiting?: boolean;
  isFailed?: boolean;
}>(({ theme, percent, isComplete, isWaiting, isFailed }) => ({
  height: "100%",
  width: `${percent}%`,
  backgroundColor: isFailed
    ? theme.colors.sysDestructiveDefault
    : isComplete
      ? theme.colors.sysSuccessDefault
      : isWaiting
        ? theme.colors.sysPrimarySubtle
        : theme.colors.sysPrimaryDefault,
  borderRadius: 16,
  transition: "width 0.3s ease, background-color 0.5s ease",
  position: "relative",
  overflow: "hidden",
  // Add shimmer effect while processing or waiting (not on failure or completion)
  ...(!isComplete &&
    !isFailed && {
      "&::after": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)`,
        backgroundSize: "200% 100%",
        animation: `${shimmer} 2s infinite`,
      },
    }),
}));

const FailedFilesCollapsibleWrapper = styled.div(({ theme }) => ({
  marginTop: 12,
  borderTop: `1px solid ${theme.colors.sysBorderDecorative}`,
}));

const FailedFilesList = styled.ul({
  listStyle: "none",
  margin: 0,
  padding: 0,
  maxHeight: 120,
  overflowY: "auto",
});

const FailedFileItem = styled.li({
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "6px 0",
});

const FailedFileName = styled.span({
  fontSize: 12,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});
